import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MAX_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MIN_SECONDS
} from '@constants/dashboard-polling-interval.constant';
import {Organization} from '@models/organization/organization';
import {TranslateService} from '@ngx-translate/core';
import {OrganizationService} from '@services/administration/organization/organization.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-organization-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {
  @Input()
  public organizationId: number = -1;
  @Input()
  public organizationName: string = '';
  @Input()
  public isAdminRole: boolean = false;
  @Input()
  public isSuperAdminRole: boolean = false;

  public pollingIntervalInSeconds: number = DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
  public readonly minPollingIntervalSeconds = DASHBOARD_POLLING_INTERVAL_MIN_SECONDS;
  public readonly maxPollingIntervalSeconds = DASHBOARD_POLLING_INTERVAL_MAX_SECONDS;
  public readonly maxPollingIntervalMinutes = DASHBOARD_POLLING_INTERVAL_MAX_SECONDS / 60;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public settingsLoaded: boolean = false;

  private organization: Organization = new Organization();
  private readonly destroy$ = new Subject<void>();
  private readonly componentName = 'OrganizationSettings';

  constructor(
      private organizationService: OrganizationService,
      private loadingIndicator: LoadingIndicatorService,
      private notification: NotificationService,
      private translate: TranslateService) {
  }

  public get canEdit(): boolean {
    return this.isAdminRole || this.isSuperAdminRole;
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public saveSettings(): void {
    if (!this.settingsLoaded || !this.canEdit || !this.isValidPollingInterval()) {
      return;
    }

    this.isSaving = true;
    this.loadingIndicator.show(this.translate.instant('Saving organization settings'));
    const organizationToSave = Object.assign(new Organization(), this.organization);
    organizationToSave.dashboardListPollingIntervalInSeconds = this.pollingIntervalInSeconds;
    this.organizationService.saveOrganization(organizationToSave).pipe(takeUntil(this.destroy$)).subscribe({
      next: (savedOrganization: Organization) => {
        this.organization = Object.assign(new Organization(), savedOrganization || organizationToSave);
        this.pollingIntervalInSeconds = this.organization.dashboardListPollingIntervalInSeconds || this.pollingIntervalInSeconds;
        this.isSaving = false;
        this.loadingIndicator.hide();
        this.notification.success(this.translate.instant('Organization settings saved.'), this.componentName, false);
      },
      error: (error) => {
        this.isSaving = false;
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error saving organization settings.'), error, this.componentName, true);
      },
    });
  }

  public isValidPollingInterval(): boolean {
    return Number.isFinite(this.pollingIntervalInSeconds)
      && this.pollingIntervalInSeconds >= this.minPollingIntervalSeconds
      && this.pollingIntervalInSeconds <= this.maxPollingIntervalSeconds;
  }

  private loadSettings(): void {
    if (this.organizationId <= 0) {
      return;
    }

    this.isLoading = true;
    this.loadingIndicator.show(this.translate.instant('Loading organization settings'));
    this.organizationService.getOrganizationDetails(this.organizationId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (organization: Organization) => {
        this.organization = Object.assign(new Organization(), organization);
        const configuredInterval = Number(this.organization.dashboardListPollingIntervalInSeconds);
        this.pollingIntervalInSeconds = Number.isFinite(configuredInterval)
          && configuredInterval >= this.minPollingIntervalSeconds
          && configuredInterval <= this.maxPollingIntervalSeconds
          ? configuredInterval
          : DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
        this.settingsLoaded = true;
        this.isLoading = false;
        this.loadingIndicator.hide();
      },
      error: (error) => {
        this.isLoading = false;
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error loading organization settings.'), error, this.componentName, true);
      },
    });
  }
}
